import React, { useState } from 'react';
import { Box, Typography, IconButton, Grid, Fade, Snackbar } from '@material-ui/core';
import SecurityIcon from '@material-ui/icons/Security';
import { useDispatch, useSelector } from 'react-redux';
import {
    _holderPublicKey,
    _signerPrivateKey,
    _jsonValue,
    setVC,
    _vc,
    setHolderPublicKey,
    setSignerPrivateKey,
    _privateKeyEncoding,
    setPrivateKeyEncoding
} from '../../../redux/issueSlice';
import { setJsonValue } from '../../../redux/issueSlice';
import _ from 'lodash';
import { verifiable } from 'sd-vc-lib';
import documentLoader from '../../../utils/document-loader';
import { decodeBase64VC } from '../../../utils/decode-vc';
import { WizardButton } from '../../ui/WizardButton';
import FieldInput from '../../ui/FeildInput';
import { OutputViewToggle } from '../../ui/ActionBtns';
import CustomizeJsonEditor from '../../ui/CustomizeJsonEditor';
import SectionDivider from '../../ui/SectionDivider';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { ViewMode } from '../types/types';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import { getSignErrorMessage } from '../utils/getSignErrorMessage';
import JsonViewer from '../../ui/JsonViwer';
import useClipboard from '../../hooks/useClipboard';
import OutputPanel from '../../ui/OutputPanel';
import { resolvePublicKeyFromDID } from '../utils/publicKeyResolver';
import { hexConverter, hexToEncoding } from '../utils/hexConverter';

export default function CredentialSign({ onSign }: { onSign?: () => void }) {
    const dispatch = useDispatch();
    const signerPrivateKey = useSelector(_signerPrivateKey);
    const privateKeyEncoding = useSelector(_privateKeyEncoding);
    const vc = useSelector(_vc);
    const holderPublicKey = useSelector(_holderPublicKey);

    const credentialJson = useSelector(_jsonValue) as object;
    const isEmpty = !credentialJson || Object.keys(credentialJson).length === 0;

    const [signerEncoding, setSignerEncoding] = useState<'Base58' | 'Base64' | 'Hex'>(
        privateKeyEncoding || 'Base58'
    );

    const [holderEncoding, setHolderEncoding] = useState<'Base58' | 'Base64' | 'Hex'>('Hex');

    const [viewMode, setViewMode] = useState<ViewMode>('encoded');

    const [draftJson, setDraftJson] = useState<string>('');
    const [isEditing, setIsEditing] = useState(false);

    const canSign = Boolean(holderPublicKey?.trim()) && Boolean(signerPrivateKey?.trim());

    const [signError, setSignError] = useState<string | null>(null);

    const { snackBarState, copyToClipboard, handleClose } = useClipboard();

    async function sign() {
        setSignError(null);
        const credential: any = _.cloneDeep(credentialJson);
        try {
            const decodedSignerKey = hexConverter(signerPrivateKey, signerEncoding);

            const vc = await verifiable.credential.create({
                credential,
                issuerPrivateKey: decodedSignerKey,
                issuanceDate: credential?.issuanceDate || new Date().toISOString(),
                documentLoader
            });
            dispatch(setVC(btoa(JSON.stringify(vc))));
            onSign?.();
        } catch (e) {
            console.error(e);
            const message = getSignErrorMessage(e, 'Sign');
            setSignError(message);
            dispatch(setVC(''));
        }
    }

    const decodedVC = React.useMemo(() => decodeBase64VC(vc), [vc]);

    const displayedHolderPublicKey = React.useMemo(() => {
        if (!holderPublicKey) return '';
        try {
            return hexToEncoding(holderPublicKey, holderEncoding);
        } catch {
            return holderPublicKey;
        }
    }, [holderPublicKey, holderEncoding]);

    React.useEffect(() => {
        const retrieveHolderPubKey = async () => {
            const holderDid = (credentialJson as any)?.credentialSubject?.holder;

            if (holderDid) {
                const holderPublicKey = await resolvePublicKeyFromDID(holderDid);
                dispatch(setHolderPublicKey(holderPublicKey));
            }
        };

        retrieveHolderPubKey();
    }, [credentialJson, dispatch]);

    return (
        <div>
            <SectionDivider />
            <Grid container spacing={3} alignItems="stretch">
                {/* LEFT PANEL */}
                <Grid item xs={12} md={7}>
                    {/* Header */}
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12
                        }}>
                        <Typography style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            Credential Payload
                        </Typography>
                        {!isEmpty && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    backgroundColor: '#e8faf7',
                                    color: '#2ec4b6',
                                    border: '1px solid #b2ede8',
                                    borderRadius: 6,
                                    padding: '2px 10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}>
                                + FROM STEP 1
                            </span>
                        )}
                        {credentialJson && !isEditing && (
                            <Box style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        copyToClipboard(JSON.stringify(credentialJson, null, 2))
                                    }
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 6,
                                        padding: 6,
                                        backgroundColor: '#fff'
                                    }}>
                                    <FileCopyOutlinedIcon style={{ fontSize: 16, color: '#555' }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* Content wrapper */}
                    <Box style={{ position: 'relative' }}>
                        {isEmpty || isEditing ? (
                            <CustomizeJsonEditor
                                value={draftJson}
                                onChange={setDraftJson}
                                onJsonEntered={(json) => {
                                    dispatch(setJsonValue(json));
                                    setIsEditing(false);
                                }}
                            />
                        ) : (
                            <Box>
                                <Box>
                                    <JsonViewer content={credentialJson} />
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    style={{ marginTop: 10 }}>
                                    <WizardButton
                                        variant="outlined"
                                        style={{ fontSize: 12 }}
                                        onClick={() => {
                                            setDraftJson(JSON.stringify(credentialJson, null, 2));
                                            setIsEditing(true);
                                        }}>
                                        Edit JSON
                                    </WizardButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>
                {/* RIGHT PANEL - Keys & Sign */}
                <Grid item xs={12} md={5} style={{ display: 'flex' }}>
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            width: '100%',
                            gap: 20
                        }}>
                        <Box style={{ marginBottom: 20 }}>
                            <Typography
                                style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    marginBottom: 8
                                }}>
                                Keys Encoding
                            </Typography>
                            <SectionDivider />
                            <Box style={{ marginBottom: 20 }}>
                                <Typography
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 8,
                                        background: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 8,
                                        padding: '9px 12px'
                                    }}>
                                    <InfoOutlined
                                        style={{
                                            fontSize: 14,
                                            color: '#999',
                                            flexShrink: 0,
                                            marginTop: 2
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: '#888',
                                            lineHeight: 1.6,
                                            fontStyle: 'italic'
                                        }}>
                                        Add your signer's private key to sign the credential. Switch
                                        between Base58, Base64, and Hex to match your key format.
                                    </span>
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 6 }}>
                                <Typography style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                    Signer's Private Key
                                </Typography>

                                <OutputViewToggle
                                    tab={
                                        signerEncoding === 'Base58'
                                            ? 0
                                            : signerEncoding === 'Base64'
                                              ? 1
                                              : 2
                                    }
                                    onTabChange={(idx) => {
                                        const val =
                                            idx === 0 ? 'Base58' : idx === 1 ? 'Base64' : 'Hex';
                                        setSignerEncoding(val);
                                        dispatch(setPrivateKeyEncoding(val));
                                    }}
                                    options={[
                                        { label: 'Base58' },
                                        { label: 'Base64' },
                                        { label: 'Hex' }
                                    ]}
                                />
                            </Box>

                            <FieldInput
                                value={signerPrivateKey}
                                placeholder="Signer private key..."
                                onChange={(v) => dispatch(setSignerPrivateKey(v))}
                                multiline
                                rows={3}
                                style={{ width: '100%', border: '1px solid #1eb89a' }}
                            />
                        </Box>
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 6 }}>
                                <Typography style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                    Holder's Public Key
                                </Typography>
                                <OutputViewToggle
                                    tab={
                                        holderEncoding === 'Base58'
                                            ? 0
                                            : holderEncoding === 'Base64'
                                              ? 1
                                              : 2
                                    }
                                    onTabChange={(idx) => {
                                        const val =
                                            idx === 0 ? 'Base58' : idx === 1 ? 'Base64' : 'Hex';
                                        setHolderEncoding(val);
                                    }}
                                    options={[
                                        { label: 'Base58' },
                                        { label: 'Base64' },
                                        { label: 'Hex' }
                                    ]}
                                />
                            </Box>
                            <FieldInput
                                value={displayedHolderPublicKey}
                                placeholder="Holder's public key"
                                onChange={() => {}}
                                multiline
                                readOnly
                                rows={3}
                                style={{
                                    width: '100%',
                                    background: '#f5f5f5',
                                    border: '1px solid #1eb89a',
                                    color: '#64748b'
                                }}
                            />
                            <Typography
                                variant="caption"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    color: '#2ec4b6'
                                }}>
                                Auto-generated from holder’s DID
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                {/* Sign button */}
                <Grid item xs={12}>
                    <WizardButton
                        variant="primary"
                        disabled={!canSign}
                        onClick={sign}
                        endIcon={<SecurityIcon style={{ fontSize: '1.1rem' }} />}
                        style={{ marginTop: 0 }}
                        fullWidth>
                        Next: Sign Credential
                    </WizardButton>
                </Grid>

                {/* Signed VC output */}
                <Grid item xs={12}>
                    {vc && !signError && (
                        <OutputPanel
                            title="Signed Verifiable Credential"
                            encodedValue={vc}
                            jsonValue={decodedVC}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            onCopy={copyToClipboard}
                            maxHeight={480}
                        />
                    )}
                    {signError && (
                        <Box
                            style={{
                                padding: 12,
                                background: '#fff4f4',
                                border: '1px solid #ffcccc',
                                color: '#d32f2f',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                            <Typography style={{ fontWeight: 400 }}>{signError}</Typography>

                            <ErrorIcon style={{ marginLeft: 12 }} />
                        </Box>
                    )}
                </Grid>
            </Grid>

            <Snackbar
                open={snackBarState.open}
                onClose={handleClose}
                TransitionComponent={Fade}
                message={snackBarState.text}
                autoHideDuration={5000}
            />
        </div>
    );
}
